import { FC, HTMLAttributes } from 'react';

import { Button, Card, Flex, Typography } from 'antd';
import cn from 'classnames';

import WalletIcon from 'assets/images/finances/wallet.svg?react';
import { useBalance } from 'entities/User/hooks/useBalance';
import { EYmEvents, EYmEventsO1, EYmEventsO2 } from 'shared/typings';
import { formatNumber, sendYmEvent } from 'shared/utils';

import { BalanceItem } from '../BalanceItem/BalanceItem';
import { Withdrawal } from '../Withdrawal/Withdrawal';

import styles from './Balance.module.scss';

const { Text } = Typography;

interface IBalanceProps extends HTMLAttributes<HTMLDivElement> {
	type?: 'short' | 'extend';
}

export const Balance: FC<IBalanceProps> = ({ className, type = 'short', ...props }) => {
	const {
		t,
		token,
		isMd,
		modalIsOpen,
		appConfig,
		currenciesMap,
		isLoading,
		checkedWithdrawal,
		addWithdrawal,
		setModalIsOpen,
	} = useBalance();

	if (!appConfig?.user?.balance) {
		return null;
	}

	const {
		user: {
			balance: { approved_amount, balance_amount, currency },
		},
	} = appConfig;

	if (type === 'short') {
		return (
			<Flex {...props} vertical className={cn(className, styles.short)}>
				<Text type="secondary" style={{ fontSize: 10 }}>
					{t('Доступны для вывода')}
				</Text>
				<Text type="success" strong style={{ fontSize: 12 }}>
					{formatNumber(balance_amount)} {currenciesMap[currency] || currency}
				</Text>
			</Flex>
		);
	}

	if (type === 'extend') {
		return (
			<>
				<Withdrawal
					open={modalIsOpen}
					onCancel={() => setModalIsOpen(false)}
					withdrawal={checkedWithdrawal}
					currency={currency}
				/>

				<Flex {...props} gap={16} className={styles.extend}>
					<Card className={styles.left} styles={{ body: { padding: 16 } }}>
						<Flex className={styles.balance}>
							<BalanceItem type="approve" balance={balance_amount} currency={currency} />
							<BalanceItem type="hold" balance={approved_amount} currency={currency} />

							<Button
								type="primary"
								size={isMd ? 'small' : 'middle'}
								onClick={() => {
									addWithdrawal();
									sendYmEvent({
										event: EYmEvents.CLICK_SEND_TO_WALLET,
										o1: [EYmEventsO1.ACCOUNT, EYmEventsO1.PUBLISHER, EYmEventsO1.FINANCE],
										o2: EYmEventsO2.FINANCE,
										isMarketing: false,
									});
								}}
								disabled={isLoading || !appConfig.payouts_available || !Number(balance_amount)}
								loading={isLoading}>
								{t('Перевести на кошелек')}
							</Button>
						</Flex>
					</Card>

					<Card
						className={styles.right}
						style={{ borderColor: token.colorPrimary }}
						styles={{ body: { padding: 16, height: '100%' } }}>
						<Flex className={styles.wallet}>
							<Flex gap={8} align="center">
								<WalletIcon />

								<Flex vertical>
									<Typography.Text type="secondary" style={{ fontSize: 12 }}>
										{t('Ваш кошелек')}
									</Typography.Text>
									<Typography.Text strong style={{ fontSize: 16 }}>
										Affilead Wallet
									</Typography.Text>
								</Flex>
							</Flex>

							<Button
								size={isMd ? 'small' : 'middle'}
								onClick={() => {
									sendYmEvent({
										event: EYmEvents.CLICK_GO_TO_WALLET,
										o1: [EYmEventsO1.ACCOUNT, EYmEventsO1.PUBLISHER, EYmEventsO1.FINANCE],
										o2: EYmEventsO2.FINANCE,
										isMarketing: false,
									});
								}}
								href={import.meta.env.VITE_WALLET_URL}
								target="_blank">
								{t('Перейти в Wallet')}
							</Button>
						</Flex>
					</Card>
				</Flex>
			</>
		);
	}
};
